import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import common from '@/i18n/locales/en/common.json'
import auth from '@/i18n/locales/en/auth.json'
import errors from '@/i18n/locales/en/errors.json'
import onboarding from '@/i18n/locales/en/onboarding.json'
import people from '@/i18n/locales/en/people.json'
import peopleAdmin from '@/i18n/locales/en/peopleAdmin.json'
import clients from '@/i18n/locales/en/clients.json'
import orgSettings from '@/i18n/locales/en/orgSettings.json'
import audit from '@/i18n/locales/en/audit.json'
import notifications from '@/i18n/locales/en/notifications.json'
import messages from '@/i18n/locales/en/messages.json'
import invites from '@/i18n/locales/en/invites.json'
import portfolio from '@/i18n/locales/en/portfolio.json'
import projectWorkspace from '@/i18n/locales/en/projectWorkspace.json'
import myWork from '@/i18n/locales/en/myWork.json'
// PX-W6.4 (TASK-191D0FE9) — the commercial spine's namespace.
import commercial from '@/i18n/locales/en/commercial.json'
// PX-W6.5 (TASK-AAB07DC3) — the configuration surfaces' namespaces.
import modules from '@/i18n/locales/en/modules.json'
import connectors from '@/i18n/locales/en/connectors.json'
import runtime from '@/i18n/locales/en/runtime.json'
import agents from '@/i18n/locales/en/agents.json'
import playbooks from '@/i18n/locales/en/playbooks.json'
// TASK-505148A8 — the navigation model's labels (rail, switcher, Home cards).
import nav from '@/i18n/locales/en/nav.json'
import commonNl from '@/i18n/locales/nl/common.json'
import authNl from '@/i18n/locales/nl/auth.json'
import errorsNl from '@/i18n/locales/nl/errors.json'
import onboardingNl from '@/i18n/locales/nl/onboarding.json'
import peopleNl from '@/i18n/locales/nl/people.json'
import peopleAdminNl from '@/i18n/locales/nl/peopleAdmin.json'
import clientsNl from '@/i18n/locales/nl/clients.json'
import orgSettingsNl from '@/i18n/locales/nl/orgSettings.json'
import auditNl from '@/i18n/locales/nl/audit.json'
import notificationsNl from '@/i18n/locales/nl/notifications.json'
import messagesNl from '@/i18n/locales/nl/messages.json'
import invitesNl from '@/i18n/locales/nl/invites.json'
import portfolioNl from '@/i18n/locales/nl/portfolio.json'
import projectWorkspaceNl from '@/i18n/locales/nl/projectWorkspace.json'
import myWorkNl from '@/i18n/locales/nl/myWork.json'
import commercialNl from '@/i18n/locales/nl/commercial.json'
import modulesNl from '@/i18n/locales/nl/modules.json'
import connectorsNl from '@/i18n/locales/nl/connectors.json'
import runtimeNl from '@/i18n/locales/nl/runtime.json'
import agentsNl from '@/i18n/locales/nl/agents.json'
import playbooksNl from '@/i18n/locales/nl/playbooks.json'
import navNl from '@/i18n/locales/nl/nav.json'

/**
 * i18n bootstrap (PX-W6.1) — EN + NL with `fallbackLng: 'en'`, structured like
 * xelerate-ai's `i18n/config.ts` with the px namespaces: `common`, `auth`,
 * `errors`, `onboarding`.
 *
 * `src/i18n/localeParity.test.ts` enforces key parity across the two locales,
 * so a key that exists in one and not the other fails the suite rather than
 * silently falling back at runtime (AC-8).
 */

/** Web-storage key the selected UI locale is persisted under. */
const LOCALE_STORAGE_KEY = 'px_locale'
/** The locales this app ships resources for. */
const SUPPORTED_LOCALES = ['en', 'nl'] as const

/** Read the persisted locale (defaults to 'en'); storage failures fall back. */
function readStoredLocale(): string {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored
    }
  } catch {
    // Ignore storage access errors (private mode) — fall through to 'en'.
  }
  return 'en'
}

/** Reflect the active locale on `<html lang="…">` for a11y + hyphenation. */
function applyDocumentLang(lng: string): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng
  }
}

i18n.use(initReactI18next).init({
  resources: {
    en: {
      common,
      auth,
      errors,
      onboarding,
      people,
      peopleAdmin,
      clients,
      orgSettings,
      audit,
      notifications,
      messages,
      invites,
      portfolio,
      projectWorkspace,
      myWork,
      commercial,
      modules,
      connectors,
      runtime,
      agents,
      playbooks,
      nav
    },
    nl: {
      common: commonNl,
      auth: authNl,
      errors: errorsNl,
      onboarding: onboardingNl,
      people: peopleNl,
      peopleAdmin: peopleAdminNl,
      clients: clientsNl,
      orgSettings: orgSettingsNl,
      audit: auditNl,
      notifications: notificationsNl,
      messages: messagesNl,
      invites: invitesNl,
      portfolio: portfolioNl,
      projectWorkspace: projectWorkspaceNl,
      myWork: myWorkNl,
      commercial: commercialNl,
      modules: modulesNl,
      connectors: connectorsNl,
      runtime: runtimeNl,
      agents: agentsNl,
      playbooks: playbooksNl,
      nav: navNl
    }
  },
  lng: readStoredLocale(),
  fallbackLng: 'en',
  defaultNS: 'common',
  interpolation: {
    escapeValue: false
  }
})

// Reflect the initial locale on <html lang>, then keep it (and the persisted
// preference) in sync on every language change. A language switcher just calls
// `i18n.changeLanguage(...)` — this listener owns the side effects. (A locale
// preference is not a credential; I-1 covers credentials only.)
applyDocumentLang(i18n.language)
i18n.on('languageChanged', (lng) => {
  applyDocumentLang(lng)
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, lng)
  } catch {
    // Ignore storage write failures (private mode).
  }
})

export default i18n
export { LOCALE_STORAGE_KEY, SUPPORTED_LOCALES }
